import { createTheme, ThemeOptions } from '@mui/material/styles';
import { tokens } from './tokens';

/**
 * Light Theme Configuration
 * 
 * Uses design tokens to create a consistent light theme for the application.
 */
export const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: tokens.colors.primary[500],
      light: tokens.colors.primary[400],
      dark: tokens.colors.primary[700],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.colors.secondary[500],
      light: tokens.colors.secondary[400],
      dark: tokens.colors.secondary[700],
      contrastText: '#FFFFFF',
    },
    success: {
      main: tokens.colors.success.main,
      light: tokens.colors.success.light,
      dark: tokens.colors.success.dark,
      contrastText: tokens.colors.success.contrastText,
    },
    warning: {
      main: tokens.colors.warning.main,
      light: tokens.colors.warning.light,
      dark: tokens.colors.warning.dark,
      contrastText: tokens.colors.warning.contrastText,
    },
    error: {
      main: tokens.colors.error.main,
      light: tokens.colors.error.light,
      dark: tokens.colors.error.dark,
      contrastText: tokens.colors.error.contrastText,
    },
    info: {
      main: tokens.colors.info.main,
      light: tokens.colors.info.light,
      dark: tokens.colors.info.dark,
      contrastText: tokens.colors.info.contrastText,
    },
    background: {
      default: tokens.colors.neutral[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: tokens.colors.neutral[900],
      secondary: tokens.colors.neutral[600],
      disabled: tokens.colors.neutral[600], // Changed from 400 for better contrast
    },
    divider: tokens.colors.neutral[200],
    action: {
      active: tokens.colors.neutral[600],
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(25, 118, 210, 0.08)',
      disabled: tokens.colors.neutral[300],
      disabledBackground: tokens.colors.neutral[100],
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.primary,
    fontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: tokens.typography.fontWeight.regular,
    fontWeightMedium: tokens.typography.fontWeight.medium,
    fontWeightBold: tokens.typography.fontWeight.bold,
    h1: {
      fontSize: tokens.typography.fontSize['4xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h2: {
      fontSize: tokens.typography.fontSize['3xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h3: {
      fontSize: tokens.typography.fontSize['2xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h4: {
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h5: {
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h6: {
      fontSize: tokens.typography.fontSize.md,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body1: {
      fontSize: tokens.typography.fontSize.md,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body2: {
      fontSize: tokens.typography.fontSize.sm,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    caption: {
      fontSize: tokens.typography.fontSize.xs,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    button: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.medium,
      textTransform: 'none',
    },
  },
  spacing: tokens.spacing.unit,
  shape: {
    borderRadius: parseInt(tokens.borderRadius.md),
  },
  shadows: [
    'none',
    tokens.shadows.sm,
    tokens.shadows.sm,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.md,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.lg,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows.xl,
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
    tokens.shadows['2xl'],
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Global focus indicator styles for accessibility
        '*:focus-visible': {
          outline: `3px solid ${tokens.colors.primary[500]}`,
          outlineOffset: '2px',
        },
        // Ensure all interactive elements are keyboard accessible
        'a, button, input, select, textarea, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])': {
          '&:focus-visible': {
            outline: `3px solid ${tokens.colors.primary[500]}`,
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.colors.primary[500], // Use main navy color
          boxShadow: tokens.shadows.md,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: `1px solid ${tokens.colors.neutral[200]}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          margin: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          boxShadow: tokens.shadows.md,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          textTransform: 'none',
          fontWeight: tokens.typography.fontWeight.medium,
          padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: tokens.borderRadius.md,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.md,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
        },
      },
    },
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: tokens.zIndex.sticky,
    drawer: tokens.zIndex.dropdown,
    modal: tokens.zIndex.modal,
    snackbar: tokens.zIndex.toast,
    tooltip: tokens.zIndex.tooltip,
  },
};

export const lightTheme = createTheme(lightThemeOptions);
